import { useState } from "react";
import type { MonitorSortOrder, TransportTrackMonitorVo } from "@/api/transportTrackMonitorTypes";
import captureLocationIcon from "./assets/capture-location.svg";
import captureTimeIcon from "./assets/capture-time.svg";
import { NOT_DATA_IMAGE } from "./notDataImage";
import { formatCaptureTime, getCaptureCategoryOption, getCaptureLocation, getCaptureReasons } from "./presentation";

interface CaptureSideListProps {
    records: TransportTrackMonitorVo[];
    total: number;
    selectedCaptureId: string;
    sortOrder: MonitorSortOrder;
    loading: boolean;
    onSelect: (id: string) => void;
    onToggleSort: () => void;
}

/** 旧版 CaptureSideList 的 React 等价组件。 */
export function CaptureSideList({
    records,
    total,
    selectedCaptureId,
    sortOrder,
    loading,
    onSelect,
    onToggleSort
}: CaptureSideListProps) {
    const [failedImageIds, setFailedImageIds] = useState(() => new Set<string>());
    const markImageFailed = (id: string) => setFailedImageIds(current => new Set(current).add(id));

    return (
        <aside className="capture-side-list" aria-label="地图抓拍列表" aria-busy={loading}>
            <div className="capture-side-list__header">
                <div className="capture-side-list__title">
                    <span>抓拍列表</span>
                    <span className="capture-side-list__count">
                        共<strong>{total}</strong>张抓拍
                    </span>
                </div>
                <button
                    type="button"
                    className="capture-side-list__sort"
                    aria-label={sortOrder === "DESC" ? "侧栏抓拍时间倒序" : "侧栏抓拍时间正序"}
                    onClick={onToggleSort}
                >
                    时间排序
                    <svg className="capture-side-list__sort-icon" viewBox="0 0 16 16" aria-hidden="true">
                        <path
                            className={sortOrder === "ASC" ? "is-active" : ""}
                            d="M7.612 2.233a.5.5 0 0 1 .776 0l2.762 3.39a.5.5 0 0 1-.388.816H5.238a.5.5 0 0 1-.388-.816l2.762-3.39Z"
                        />
                        <path
                            className={sortOrder === "DESC" ? "is-active" : ""}
                            d="M7.612 13.767a.5.5 0 0 0 .776 0l2.762-3.39a.5.5 0 0 0-.388-.816H5.238a.5.5 0 0 0-.388.816l2.762 3.39Z"
                        />
                    </svg>
                </button>
            </div>
            {records.length ? (
                <div className="capture-side-list__body">
                    {records.map(record => {
                        const id = String(record.id);
                        const category = getCaptureCategoryOption(record);
                        const imageUrl = record.captureFile?.path || "";
                        return (
                            <button
                                key={id}
                                type="button"
                                className={`capture-side-list__item${id === selectedCaptureId ? " is-active" : ""}`}
                                aria-pressed={id === selectedCaptureId}
                                onClick={() => onSelect(id)}
                            >
                                <span className="capture-side-list__thumbnail-wrap">
                                    {imageUrl && !failedImageIds.has(id) ? (
                                        <img
                                            className="capture-side-list__thumbnail"
                                            src={imageUrl}
                                            alt="抓拍缩略图"
                                            onError={() => markImageFailed(id)}
                                        />
                                    ) : (
                                        <span className="capture-side-list__image-fallback">
                                            <svg viewBox="0 0 24 24" aria-hidden="true">
                                                <path d="M9 4 7.5 6H5a3 3 0 0 0-3 3v8a3 3 0 0 0 3 3h14a3 3 0 0 0 3-3V9a3 3 0 0 0-3-3h-2.5L15 4H9Zm3 4.5a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9Z" />
                                            </svg>
                                            加载失败
                                        </span>
                                    )}
                                </span>
                                <span className="capture-side-list__info">
                                    <span className="capture-side-list__reason">
                                        <span>{getCaptureReasons(record)}</span>
                                        <span
                                            className="capture-side-list__category"
                                            style={{ color: category.color, backgroundColor: `${category.color}1a` }}
                                        >
                                            {category.label}
                                        </span>
                                    </span>
                                    <span className="capture-side-list__meta-row">
                                        <img
                                            src={captureTimeIcon}
                                            className="capture-side-list__meta-icon"
                                            alt=""
                                            aria-hidden="true"
                                        />
                                        <span className="capture-side-list__time">
                                            {formatCaptureTime(record.displayTime || record.captureTime)}
                                        </span>
                                    </span>
                                    <span className="capture-side-list__meta-row">
                                        <img
                                            src={captureLocationIcon}
                                            className="capture-side-list__meta-icon"
                                            alt=""
                                            aria-hidden="true"
                                        />
                                        <span className="capture-side-list__location">
                                            {getCaptureLocation(record).split("（")[0]}
                                        </span>
                                    </span>
                                </span>
                            </button>
                        );
                    })}
                </div>
            ) : (
                <div className="capture-side-list__empty">
                    <img src={NOT_DATA_IMAGE} alt="" />
                    <span>暂无数据</span>
                </div>
            )}
        </aside>
    );
}
